// OliverBuildFrame.tsx

import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import DeviceSkin from "@builder/components/app/OliverBuildFrame/DeviceSkin";
import classNames from "classnames";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { selectNode } from "../../../store/slices/BuilderSlice/builderSlice";
import { updateTranslate } from "@builder/store/slices/frameTransformSlice";
import useBuilderGlobalColors from "./hooks/useBuilderGlobalColors";
import "./style.scss";
import TransformWrapper from "./TransformWrapper";
import { getCurrentLayer } from "@builder/store/slices/BuilderSlice/builderSelectors";
import ContextMenuBuildFrame from "../ContextMenus/ContextMenuBuildFrame";
import RenderVariationNodes from "./RenderVariationNodes";
import RenderFromNode from "./RenderFromNode";
const OliverBuildFrame: React.FC = () => {
    const dispatch = useAppDispatch();
    const [moveFrame, setMoveFrame] = useState<boolean>(false);
    const frameRef = useRef<HTMLDivElement>(null);
    const buildElementRef = useRef<HTMLDivElement | null>(null);
    const isScrollingProgrammatically = useRef<boolean>(false);
    const lastScrollX = useRef<number>(0);
    const lastScrollY = useRef<number>(0);
    const hasInitialized = useRef<boolean>(false);
    const dragRafId = useRef<number | null>(null);
    const accumulatedDragDeltaX = useRef<number>(0);
    const accumulatedDragDeltaY = useRef<number>(0);
    const lastProcessedClientX = useRef<number>(0);
    const lastProcessedClientY = useRef<number>(0);
    const currentTranslateX = useRef<number>(0);
    const currentTranslateY = useRef<number>(0);
    const isInitialMount = useRef<boolean>(true);
    const previousScale = useRef<number | null>(null);
    const deviceSkin = useAppSelector(state => state.frameTransform.deviceSkin);
    const customCanvasBGColor = useAppSelector(state => state.systemUI.customCanvasBG) || "var(--theme-second-bg)";
    const currentLayer = useAppSelector(getCurrentLayer);
    const conditionalVariation = useAppSelector(state => state.builder.conditionalVariationMode);
    const transform = useAppSelector(state => state.frameTransform);
    const frameSize = useAppSelector(state => state.builder.frameSize);

    // Calculate scroll area size: minimum oliver-build-frame viewport size + scaled build size on both sides
    const scale = transform.scale.value;

    // Initialize previousScale on first render
    if (previousScale.current === null) {
        previousScale.current = scale;
    }

    // Track viewport dimensions of oliver-build-frame element
    const [viewportWidth, setViewportWidth] = useState<number>(0);
    const [viewportHeight, setViewportHeight] = useState<number>(0);

    // Calculate scroll area dimensions
    const [measuredHeight, setMeasuredHeight] = useState<number>(frameSize.x);

    // Measure oliver-build-frame viewport dimensions
    useEffect(() => {
        if (!frameRef.current) return;

        const frame = frameRef.current;
        const updateViewportSize = () => {
            setViewportWidth(frame.clientWidth);
            setViewportHeight(frame.clientHeight);
        };

        // Initial measurement
        updateViewportSize();

        // Use ResizeObserver to track viewport size changes
        const resizeObserver = new ResizeObserver(() => {
            updateViewportSize();
        });

        resizeObserver.observe(frame);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    // Measure actual height for fit-content
    useEffect(() => {
        if (frameSize.y !== "fit-content") {
            setMeasuredHeight(frameSize.y);
        }
    }, [frameSize.y]);

    // Use ResizeObserver to measure height for fit-content
    useEffect(() => {
        if (frameSize.y !== "fit-content" || !buildElementRef.current) return;

        const element = buildElementRef.current;
        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const height = entry.contentRect.height;
                if (height > 0) {
                    setMeasuredHeight(height);
                }
            }
        });

        resizeObserver.observe(element);

        return () => {
            resizeObserver.disconnect();
        };
    }, [frameSize.y, buildElementRef.current]);

    // Callback ref to set the build element ref
    const handleBuildRef = (ref: HTMLDivElement | null) => {
        buildElementRef.current = ref;
        if (ref && frameSize.y === "fit-content") {
            // Use requestAnimationFrame to ensure DOM is ready
            requestAnimationFrame(() => {
                const height = ref.offsetHeight;
                if (height > 0) {
                    setMeasuredHeight(height);
                }
            });
        }
    };

    // Calculate scaled build dimensions
    const buildWidth = frameSize.x * scale;
    const buildHeight = (frameSize.y === "fit-content" ? measuredHeight : frameSize.y) * scale;

    // Scroll area: minimum oliver-build-frame viewport size + scaled build size on both sides
    // This ensures the entire build can be scrolled to the center of the screen
    // Use actual frame dimensions if state is not ready yet (fallback to build dimensions * 3 as minimum)
    const effectiveViewportWidth = viewportWidth > 0 ? viewportWidth : frameRef.current?.clientWidth || buildWidth * 3;
    const effectiveViewportHeight = viewportHeight > 0 ? viewportHeight : frameRef.current?.clientHeight || buildHeight * 3;
    const scrollAreaWidth = Math.max(effectiveViewportWidth, effectiveViewportWidth + buildWidth * 2);
    const scrollAreaHeight = Math.max(effectiveViewportHeight, effectiveViewportHeight + buildHeight * 2);

    useBuilderGlobalColors();

    // Initialize scroll position on mount only
    useLayoutEffect(() => {
        if (!frameRef.current || !isInitialMount.current) return;

        const frame = frameRef.current;

        // Use actual frame dimensions (not state, which might be stale)
        const actualViewportWidth = frame.clientWidth;
        const actualViewportHeight = frame.clientHeight;

        // Guard: Don't center if viewport dimensions are not ready
        if (actualViewportWidth === 0 || actualViewportHeight === 0) {
            return;
        }

        // Calculate scroll area with actual dimensions
        const scrollAreaWidth = Math.max(actualViewportWidth, actualViewportWidth + buildWidth * 2);
        const scrollAreaHeight = Math.max(actualViewportHeight, actualViewportHeight + buildHeight * 2);
        const contentCenterX = scrollAreaWidth / 2;
        const contentCenterY = scrollAreaHeight / 2;
        const scrollX = contentCenterX - actualViewportWidth / 2;
        const scrollY = contentCenterY - actualViewportHeight / 2;

        // Center on initial mount - useLayoutEffect runs after DOM mutations, so it's safe to set scroll immediately
        isScrollingProgrammatically.current = true;
        frame.scrollLeft = scrollX;
        frame.scrollTop = scrollY;
        lastScrollX.current = scrollX;
        lastScrollY.current = scrollY;
        hasInitialized.current = true;
        isInitialMount.current = false;

        requestAnimationFrame(() => {
            isScrollingProgrammatically.current = false;
        });
    }, [scrollAreaWidth, scrollAreaHeight, buildWidth, buildHeight]);

    // Preserve viewport position when zooming (scale changes)
    useLayoutEffect(() => {
        if (!frameRef.current || !hasInitialized.current || isInitialMount.current) {
            if (previousScale.current === null) {
                previousScale.current = scale;
            }
            return;
        }

        // Only adjust if scale actually changed
        if (previousScale.current === null || previousScale.current === scale) {
            previousScale.current = scale;
            return;
        }

        const frame = frameRef.current;
        // At this point, previousScale.current is guaranteed to be a number (not null)
        const oldScale = previousScale.current!;
        const scaleChange = scale / oldScale;

        // Get current viewport center point in scroll coordinates
        const currentScrollX = frame.scrollLeft;
        const currentScrollY = frame.scrollTop;
        const viewportWidth = frame.clientWidth;
        const viewportHeight = frame.clientHeight;

        const viewportCenterX = currentScrollX + viewportWidth / 2;
        const viewportCenterY = currentScrollY + viewportHeight / 2;

        // Calculate new scroll area dimensions with new scale
        const newBuildWidth = frameSize.x * scale;
        const newBuildHeight = (frameSize.y === "fit-content" ? measuredHeight : frameSize.y) * scale;
        const newScrollAreaWidth = Math.max(viewportWidth, viewportWidth + newBuildWidth * 2);
        const newScrollAreaHeight = Math.max(viewportHeight, viewportHeight + newBuildHeight * 2);

        // Content center is at newScrollAreaWidth/2, newScrollAreaHeight/2
        const newContentCenterX = newScrollAreaWidth / 2;
        const newContentCenterY = newScrollAreaHeight / 2;

        // Calculate the offset from the old content center to the viewport center
        // We need to use the old scroll area dimensions for this calculation
        const oldBuildWidth = frameSize.x * oldScale;
        const oldBuildHeight = (frameSize.y === "fit-content" ? measuredHeight : frameSize.y) * oldScale;
        const oldScrollAreaWidth = Math.max(viewportWidth, viewportWidth + oldBuildWidth * 2);
        const oldScrollAreaHeight = Math.max(viewportHeight, viewportHeight + oldBuildHeight * 2);
        const oldContentCenterX = oldScrollAreaWidth / 2;
        const oldContentCenterY = oldScrollAreaHeight / 2;

        const offsetFromOldContentCenterX = viewportCenterX - oldContentCenterX;
        const offsetFromOldContentCenterY = viewportCenterY - oldContentCenterY;

        // The offset needs to be scaled by the scale change ratio
        // When zooming in (scale increases), the same point in content moves further from center
        const newOffsetX = offsetFromOldContentCenterX * scaleChange;
        const newOffsetY = offsetFromOldContentCenterY * scaleChange;

        // Calculate new scroll position to keep the same point at viewport center
        const newScrollX = newContentCenterX + newOffsetX - viewportWidth / 2;
        const newScrollY = newContentCenterY + newOffsetY - viewportHeight / 2;

        // Clamp scroll position
        const maxScrollX = newScrollAreaWidth - 1;
        const minScrollX = 1;
        const maxScrollY = newScrollAreaHeight - 1;
        const minScrollY = 1;
        const clampedScrollX = Math.max(minScrollX, Math.min(maxScrollX, newScrollX));
        const clampedScrollY = Math.max(minScrollY, Math.min(maxScrollY, newScrollY));

        // Update scroll position
        setTimeout(() => {
            if (frameRef.current) {
                isScrollingProgrammatically.current = true;
                frameRef.current.scrollLeft = clampedScrollX;
                frameRef.current.scrollTop = clampedScrollY;
                lastScrollX.current = clampedScrollX;
                lastScrollY.current = clampedScrollY;

                // Update translate to match new scroll position
                const translateX = clampedScrollX - newContentCenterX + viewportWidth / 2;
                const translateY = clampedScrollY - newContentCenterY + viewportHeight / 2;

                dispatch(
                    updateTranslate({
                        clientX: null,
                        clientY: null,
                        translateX,
                        translateY
                    })
                );

                requestAnimationFrame(() => {
                    isScrollingProgrammatically.current = false;
                });
            }
        }, 0);

        previousScale.current = scale;
    }, [scale, frameSize.x, frameSize.y, measuredHeight, dispatch]);

    // Sync scroll position with translate state
    useEffect(() => {
        if (!frameRef.current || isScrollingProgrammatically.current || !hasInitialized.current) return;

        const frame = frameRef.current;
        const translateX = transform.translate.x.translateX;
        const translateY = transform.translate.y.translateY;

        // Calculate scroll position based on translate
        // Content is at scrollAreaWidth/2, scrollAreaHeight/2
        // To center in viewport: scrollAreaWidth/2 - viewportWidth/2
        // Then add translate offset
        const contentCenterX = scrollAreaWidth / 2;
        const contentCenterY = scrollAreaHeight / 2;
        const viewportWidth = frame.clientWidth;
        const viewportHeight = frame.clientHeight;

        let scrollX = contentCenterX - viewportWidth / 2 + translateX;
        let scrollY = contentCenterY - viewportHeight / 2 + translateY;

        // Clamp scroll position to prevent going too far
        const maxScrollX = scrollAreaWidth - 1;
        const minScrollX = 1;
        const maxScrollY = scrollAreaHeight - 1;
        const minScrollY = 1;
        scrollX = Math.max(minScrollX, Math.min(maxScrollX, scrollX));
        scrollY = Math.max(minScrollY, Math.min(maxScrollY, scrollY));

        // Only update if scroll position differs significantly (avoid infinite loops)
        const currentScrollX = frame.scrollLeft;
        const currentScrollY = frame.scrollTop;
        const threshold = 1;

        if (Math.abs(currentScrollX - scrollX) > threshold || Math.abs(currentScrollY - scrollY) > threshold) {
            isScrollingProgrammatically.current = true;
            frame.scrollLeft = scrollX;
            frame.scrollTop = scrollY;
            lastScrollX.current = scrollX;
            lastScrollY.current = scrollY;
            requestAnimationFrame(() => {
                isScrollingProgrammatically.current = false;
            });
        }
    }, [transform.translate.x.translateX, transform.translate.y.translateY, scrollAreaWidth, scrollAreaHeight]);

    const deselectNode = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.currentTarget === e.target || (e.target as HTMLElement)?.classList.contains("build-scroll-space")) {
            dispatch(selectNode(null));
        }
    };

    const handleScroll = () => {
        if (isScrollingProgrammatically.current || !frameRef.current) return;

        const frame = frameRef.current;
        let scrollX = frame.scrollLeft;
        let scrollY = frame.scrollTop;

        // Clamp scroll position to prevent going too far
        const clampedMaxScrollX = scrollAreaWidth - 1;
        const clampedMinScrollX = 1;
        const clampedMaxScrollY = scrollAreaHeight - 1;
        const clampedMinScrollY = 1;
        scrollX = Math.max(clampedMinScrollX, Math.min(clampedMaxScrollX, scrollX));
        scrollY = Math.max(clampedMinScrollY, Math.min(clampedMaxScrollY, scrollY));

        // Only update if scroll actually changed
        if (scrollX !== lastScrollX.current || scrollY !== lastScrollY.current) {
            // If we clamped, update the scroll position
            if (scrollX !== frame.scrollLeft || scrollY !== frame.scrollTop) {
                isScrollingProgrammatically.current = true;
                frame.scrollLeft = scrollX;
                frame.scrollTop = scrollY;
                requestAnimationFrame(() => {
                    isScrollingProgrammatically.current = false;
                });
            }

            lastScrollX.current = scrollX;
            lastScrollY.current = scrollY;

            // Convert scroll position to translate (offset from center)
            // Scroll position = contentCenter - viewportSize/2 + translate
            // So: translate = scrollPosition - contentCenter + viewportSize/2
            const contentCenterX = scrollAreaWidth / 2;
            const contentCenterY = scrollAreaHeight / 2;
            const viewportWidth = frame.clientWidth;
            const viewportHeight = frame.clientHeight;

            const translateX = scrollX - contentCenterX + viewportWidth / 2;
            const translateY = scrollY - contentCenterY + viewportHeight / 2;

            // Update translate state
            dispatch(
                updateTranslate({
                    clientX: null,
                    clientY: null,
                    translateX,
                    translateY
                })
            );
        }
    };

    const handleMiddleMouseDown = (e: React.MouseEvent) => {
        if (e.button === 1 || (e.button === 0 && (e.ctrlKey || e.metaKey))) {
            setMoveFrame(true);
            e.preventDefault();

            // Cancel any pending drag animation frame
            if (dragRafId.current !== null) {
                cancelAnimationFrame(dragRafId.current);
                dragRafId.current = null;
            }

            // Reset accumulated deltas
            accumulatedDragDeltaX.current = 0;
            accumulatedDragDeltaY.current = 0;

            // Store initial mouse position as last processed position
            lastProcessedClientX.current = e.clientX;
            lastProcessedClientY.current = e.clientY;

            // Initialize current translate values from Redux state
            currentTranslateX.current = transform.translate.x.translateX;
            currentTranslateY.current = transform.translate.y.translateY;

            if (frameRef.current) {
                lastScrollX.current = frameRef.current.scrollLeft;
                lastScrollY.current = frameRef.current.scrollTop;
            }
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMiddleMouseUp);
        }
    };

    const handleMiddleMouseUp = () => {
        // Cancel any pending drag animation frame
        if (dragRafId.current !== null) {
            cancelAnimationFrame(dragRafId.current);
            dragRafId.current = null;
        }

        // Process any remaining accumulated deltas before cleanup
        if (accumulatedDragDeltaX.current !== 0 || accumulatedDragDeltaY.current !== 0) {
            processDragDeltas();
        }

        dispatch(updateTranslate({ clientX: null, clientY: null }));
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMiddleMouseUp);
        setMoveFrame(false);

        // Reset accumulated deltas
        accumulatedDragDeltaX.current = 0;
        accumulatedDragDeltaY.current = 0;
    };

    const processDragDeltas = () => {
        if (!frameRef.current) return;

        const frame = frameRef.current;

        // Invert deltas: dragging left moves content right, dragging right moves content left
        // So we subtract the delta from translate (opposite of mouse movement)
        const newTranslateX = currentTranslateX.current - accumulatedDragDeltaX.current;
        const newTranslateY = currentTranslateY.current - accumulatedDragDeltaY.current;

        // Convert translate back to scroll position
        // translate = scrollPosition - contentCenter + viewportSize/2
        // So: scrollPosition = translate + contentCenter - viewportSize/2
        const contentCenterX = scrollAreaWidth / 2;
        const contentCenterY = scrollAreaHeight / 2;
        const viewportWidth = frame.clientWidth;
        const viewportHeight = frame.clientHeight;

        let newScrollX = newTranslateX + contentCenterX - viewportWidth / 2;
        let newScrollY = newTranslateY + contentCenterY - viewportHeight / 2;

        // clamp scroll position
        const maxScrollX = scrollAreaWidth - 1;
        const minScrollX = 1;
        const maxScrollY = scrollAreaHeight - 1;
        const minScrollY = 1;

        newScrollX = Math.max(minScrollX, Math.min(maxScrollX, newScrollX));
        newScrollY = Math.max(minScrollY, Math.min(maxScrollY, newScrollY));

        // Recalculate translate from clamped scroll (to keep in sync)
        const translateX = newScrollX - contentCenterX + viewportWidth / 2;
        const translateY = newScrollY - contentCenterY + viewportHeight / 2;

        // ATOMIC UPDATE of both axes in same frame
        isScrollingProgrammatically.current = true;

        frame.scrollLeft = newScrollX;
        frame.scrollTop = newScrollY;

        lastScrollX.current = newScrollX;
        lastScrollY.current = newScrollY;

        // Update last processed client position
        const totalDeltaX = accumulatedDragDeltaX.current;
        const totalDeltaY = accumulatedDragDeltaY.current;
        lastProcessedClientX.current += totalDeltaX;
        lastProcessedClientY.current += totalDeltaY;

        // Update current translate refs for next frame
        currentTranslateX.current = translateX;
        currentTranslateY.current = translateY;

        dispatch(
            updateTranslate({
                clientX: lastProcessedClientX.current,
                clientY: lastProcessedClientY.current,
                translateX,
                translateY
            })
        );

        isScrollingProgrammatically.current = false;

        // Reset accumulated deltas
        accumulatedDragDeltaX.current = 0;
        accumulatedDragDeltaY.current = 0;
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!frameRef.current) return;

        // Calculate delta from the last processed position plus any accumulated deltas
        // This ensures we don't double-count deltas when multiple mousemove events fire before RAF
        const expectedCurrentX = lastProcessedClientX.current + accumulatedDragDeltaX.current;
        const expectedCurrentY = lastProcessedClientY.current + accumulatedDragDeltaY.current;
        const deltaX = e.clientX - expectedCurrentX;
        const deltaY = e.clientY - expectedCurrentY;

        // Accumulate deltas for smooth panning
        accumulatedDragDeltaX.current += deltaX;
        accumulatedDragDeltaY.current += deltaY;

        // Cancel any pending animation frame
        if (dragRafId.current !== null) {
            cancelAnimationFrame(dragRafId.current);
        }

        // Use requestAnimationFrame for smooth updates (same pattern as wheel handler)
        dragRafId.current = requestAnimationFrame(() => {
            processDragDeltas();
            dragRafId.current = null;
        });
    };

    // Handle smooth two-finger panning (trackpad gestures)
    useEffect(() => {
        if (!frameRef.current || deviceSkin) return;

        const frame = frameRef.current;
        let rafId: number | null = null;
        let accumulatedDeltaX = 0;
        let accumulatedDeltaY = 0;

        const handleWheel = (e: WheelEvent) => {
            // Only handle panning (not zooming - zoom uses ctrl/meta)
            if (e.ctrlKey || e.metaKey) return;

            // Check if the event target is a scrollable child element
            const target = e.target as HTMLElement;
            let shouldAllowChildScroll = false;

            if (target && target !== frame) {
                // Check if target or any parent is a scrollable element
                let element: HTMLElement | null = target;
                while (element && element !== frame) {
                    const style = window.getComputedStyle(element);
                    const overflowX = style.overflowX;
                    const overflowY = style.overflowY;
                    const isScrollableX = overflowX === "auto" || overflowX === "scroll";
                    const isScrollableY = overflowY === "auto" || overflowY === "scroll";

                    if (isScrollableX || isScrollableY) {
                        // Check if element can still scroll in the direction of the wheel event
                        const canScrollX =
                            isScrollableX &&
                            ((e.deltaX < 0 && element.scrollLeft > 0) ||
                                (e.deltaX > 0 && element.scrollLeft < element.scrollWidth - element.clientWidth));
                        const canScrollY =
                            isScrollableY &&
                            ((e.deltaY < 0 && element.scrollTop > 0) ||
                                (e.deltaY > 0 && element.scrollTop < element.scrollHeight - element.clientHeight));

                        if (canScrollX || canScrollY) {
                            shouldAllowChildScroll = true;
                            break;
                        }
                    }

                    element = element.parentElement;
                }
            }

            // If a child element can scroll, allow default behavior
            if (shouldAllowChildScroll) {
                return;
            }

            // Prevent default scrolling for frame panning
            e.preventDefault();

            // Accumulate deltas for smooth panning
            accumulatedDeltaX += e.deltaX;
            accumulatedDeltaY += e.deltaY;

            // Cancel any pending animation frame
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }

            // Use requestAnimationFrame for smooth updates
            rafId = requestAnimationFrame(() => {
                if (!frameRef.current) return;

                const frame = frameRef.current;

                // Calculate new scroll positions
                // For wheel events: positive delta = scroll down/right = increase scroll position
                let newScrollX = lastScrollX.current + accumulatedDeltaX;
                let newScrollY = lastScrollY.current + accumulatedDeltaY;

                // Clamp scroll position
                const maxScrollX = scrollAreaWidth - 1;
                const minScrollX = 1;
                const maxScrollY = scrollAreaHeight - 1;
                const minScrollY = 1;

                newScrollX = Math.max(minScrollX, Math.min(maxScrollX, newScrollX));
                newScrollY = Math.max(minScrollY, Math.min(maxScrollY, newScrollY));

                // Compute translate from scroll
                const contentCenterX = scrollAreaWidth / 2;
                const contentCenterY = scrollAreaHeight / 2;
                const viewportWidth = frame.clientWidth;
                const viewportHeight = frame.clientHeight;

                const translateX = newScrollX - contentCenterX + viewportWidth / 2;
                const translateY = newScrollY - contentCenterY + viewportHeight / 2;

                // Update scroll and translate atomically
                isScrollingProgrammatically.current = true;

                frame.scrollLeft = newScrollX;
                frame.scrollTop = newScrollY;

                lastScrollX.current = newScrollX;
                lastScrollY.current = newScrollY;

                dispatch(
                    updateTranslate({
                        clientX: null,
                        clientY: null,
                        translateX,
                        translateY
                    })
                );

                isScrollingProgrammatically.current = false;

                // Reset accumulated deltas
                accumulatedDeltaX = 0;
                accumulatedDeltaY = 0;
                rafId = null;
            });
        };

        frame.addEventListener("wheel", handleWheel, { passive: false });

        return () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
            frame.removeEventListener("wheel", handleWheel);
        };
    }, [scrollAreaWidth, scrollAreaHeight, deviceSkin, dispatch]);

    if (!currentLayer) {
        return null;
    }

    if (deviceSkin) {
        return (
            <div
                className="oliver-build-frame"
                style={{
                    maxHeight: "100%",
                    overflow: "hidden",
                    backgroundColor: customCanvasBGColor || "var(--theme-second-bg)"
                }}
            >
                <DeviceSkin>
                    <RenderFromNode />
                </DeviceSkin>
            </div>
        );
    }

    return (
        <div
            ref={frameRef}
            className={classNames("oliver-build-frame", {
                ["mouse-grab"]: moveFrame
            })}
            onClick={deselectNode}
            onMouseDown={handleMiddleMouseDown}
            onScroll={handleScroll}
            style={{
                backgroundColor: customCanvasBGColor || "var(--theme-second-bg)"
            }}
        >
            {conditionalVariation.id ? (
                <RenderVariationNodes />
            ) : (
                <TransformWrapper scrollAreaWidth={scrollAreaWidth} scrollAreaHeight={scrollAreaHeight} onBuildRef={handleBuildRef}>
                    <RenderFromNode />
                    <ContextMenuBuildFrame />
                </TransformWrapper>
            )}
        </div>
    );
};

export default OliverBuildFrame;
